import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './FunnelDiagram.css';

/**
 * FunnelDiagram - Visual representation of traffic sources → products → upgrades
 *
 * Props:
 *   products: Array of product objects with id, name, position, sale_price, is_primary_tripwire
 *   upgradePaths: Array of {from_product_id, to_product_id, conversion_rate}
 *   promotions: Array of promotions happening in the next 30 days (optional)
 *   trafficSources: Array of {name, icon, type} - social platforms, email, JV, etc.
 *   contentVolume: Object with {social, videos, emails, articles} monthly counts
 *   salesTargets: Object with entry_acquisitions {productName: count} for showing sales numbers
 */
const FunnelDiagram = ({ products = [], upgradePaths = [], promotions = [], trafficSources = [], contentVolume = null, salesTargets = null }) => {
  const navigate = useNavigate();

  // Content-based sources with volumes - these show what content we're producing
  // These are always shown with volumes from the recommended tier
  const contentSources = useMemo(() => {
    return [
      {
        name: 'Social Media',
        icon: '📱',
        type: 'organic',
        volume: contentVolume?.social || null
      },
      {
        name: 'Videos',
        icon: '🎬',
        type: 'organic',
        volume: contentVolume?.videos || null
      },
      {
        name: 'Email',
        icon: '📧',
        type: 'owned',
        volume: contentVolume?.emails || null
      },
      {
        name: 'Articles/SEO',
        icon: '📝',
        type: 'organic',
        volume: contentVolume?.articles || null
      },
    ];
  }, [contentVolume]);

  // Use content sources (which include volumes) - ignore trafficSources prop for now
  // as we want to show content production volumes, not platform-specific sources
  const sources = contentSources;

  // Organize products by position, with primary tripwire always at top of entry
  // If all products are 'entry' or null, infer positions from upgrade paths
  const productsByPosition = useMemo(() => {
    const sortWithTripwireFirst = (a, b) => {
      // Primary tripwire always first
      if (a.is_primary_tripwire) return -1;
      if (b.is_primary_tripwire) return 1;
      // Then sort by price
      return (a.sale_price || 0) - (b.sale_price || 0);
    };

    // Include both 'entry' and 'tripwire' positions as entry products
    let entry = products.filter(p => p.position === 'entry' || p.position === 'tripwire' || !p.position).sort(sortWithTripwireFirst);
    let core = products.filter(p => p.position === 'core').sort(sortWithTripwireFirst);
    let premium = products.filter(p => p.position === 'premium').sort(sortWithTripwireFirst);

    // If we have upgrade paths but no core/premium products, infer from paths
    if (upgradePaths.length > 0 && core.length === 0 && premium.length === 0) {
      // Find products that are upgrade targets (not sources of any path that leads elsewhere)
      const sourceIds = new Set(upgradePaths.map(p => p.from_product_id));
      const targetIds = new Set(upgradePaths.map(p => p.to_product_id));

      // Entry = sources that aren't targets (or tripwire)
      // Core = both source and target
      // Premium = targets that aren't sources

      const allProducts = [...entry]; // Start with all products
      entry = [];
      core = [];
      premium = [];

      allProducts.forEach(product => {
        const isSource = sourceIds.has(product.id);
        const isTarget = targetIds.has(product.id);

        if (product.is_primary_tripwire || (isSource && !isTarget)) {
          entry.push(product);
        } else if (isSource && isTarget) {
          core.push(product);
        } else if (isTarget && !isSource) {
          premium.push(product);
        } else {
          // No upgrade path info - put in entry
          entry.push(product);
        }
      });

      entry.sort(sortWithTripwireFirst);
      core.sort(sortWithTripwireFirst);
      premium.sort(sortWithTripwireFirst);
    }

    return { entry, core, premium };
  }, [products, upgradePaths]);

  // Get active promotions (next 30 days)
  const activePromos = useMemo(() => {
    const now = new Date();
    const thirtyDays = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    return promotions.filter(p => {
      const start = new Date(p.start_date);
      return start >= now && start <= thirtyDays;
    });
  }, [promotions]);

  // Build upgrade path map for drawing arrows
  const upgradeMap = useMemo(() => {
    const map = {};
    upgradePaths.forEach(path => {
      if (!map[path.from_product_id]) {
        map[path.from_product_id] = [];
      }
      map[path.from_product_id].push({
        toId: path.to_product_id,
        rate: path.conversion_rate
      });
    });
    return map;
  }, [upgradePaths]);

  // Find entry products that receive traffic (tripwires or entry position)
  const entryPoints = useMemo(() => {
    const tripwire = products.find(p => p.is_primary_tripwire);
    if (tripwire) return [tripwire];
    return productsByPosition.entry.length > 0 ? productsByPosition.entry : products.slice(0, 1);
  }, [products, productsByPosition]);

  // Check if a product has an active promo
  const getPromoForProduct = (productId) => {
    return activePromos.find(p => p.product_id === productId);
  };

  // Get sales target for a product by name
  const getSalesTarget = (productName) => {
    if (!salesTargets?.entry_acquisitions) return null;
    return salesTargets.entry_acquisitions[productName] || null;
  };

  // Calculate upgrade count from entry sales and conversion rate
  const getUpgradeCount = useMemo(() => {
    if (!salesTargets?.entry_acquisitions || upgradePaths.length === 0) return {};

    const counts = {};
    const entryProducts = productsByPosition.entry.length > 0 ? productsByPosition.entry : [];

    // For each core product, calculate expected upgrades from entry products
    productsByPosition.core.forEach(coreProduct => {
      let totalUpgrades = 0;

      entryProducts.forEach(entryProduct => {
        const path = upgradePaths.find(p =>
          p.from_product_id === entryProduct.id && p.to_product_id === coreProduct.id
        );

        if (path) {
          const entrySales = salesTargets.entry_acquisitions[entryProduct.name] || 0;
          const upgrades = Math.round(entrySales * (path.conversion_rate / 100));
          totalUpgrades += upgrades;
        }
      });

      counts[coreProduct.id] = totalUpgrades;
    });

    return counts;
  }, [salesTargets, upgradePaths, productsByPosition]);

  // Format price
  const formatPrice = (price) => {
    if (!price) return '';
    return `$${parseFloat(price).toLocaleString()}`;
  };

  if (products.length === 0) {
    return (
      <div className="funnel-diagram funnel-empty">
        <p className="text-muted text-center">Add products to see your funnel visualization</p>
      </div>
    );
  }

  return (
    <div className="funnel-diagram">
      {/* Traffic Sources Column */}
      <div className="funnel-column funnel-sources">
        <div className="column-header">
          <span className="header-icon">🎯</span>
          <span>Traffic Sources</span>
        </div>
        <div className="column-items">
          {/* Promotions as Traffic Sources - they bring in customers! */}
          {promotions.map((promo) => {
            const product = products.find(p => p.id === promo.product_id);
            const startDate = new Date(promo.start_date);
            const monthName = startDate.toLocaleString('default', { month: 'short' });
            return (
              <div
                key={`promo-${promo.id}`}
                className="funnel-node source-node promo-source clickable"
                onClick={() => navigate('/planned-promotions')}
                title="Click to edit promotions"
              >
                <span className="node-icon">{promo.promotion_type === 'lifetime_deal' ? '🏷️' : '🔥'}</span>
                <span className="node-name">{promo.promotion_name}</span>
                <span className="node-volume promo-volume">
                  <strong>{promo.estimated_sales || 0}</strong> customers
                </span>
                <span className="promo-timing-small">{monthName} {startDate.getDate()}</span>
              </div>
            );
          })}
          {/* Organic Traffic Sources */}
          {sources.map((source, idx) => (
            <div key={idx} className={`funnel-node source-node ${source.type}`}>
              <span className="node-icon">{source.icon}</span>
              <span className="node-name">{source.name}</span>
              {source.volume && (
                <span className="node-volume">{source.volume}/mo</span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Arrow: Traffic → Entry */}
      <div className="flow-arrow">
        <svg viewBox="0 0 60 40" preserveAspectRatio="xMidYMid meet">
          <defs>
            <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
              <polygon points="0 0, 10 3.5, 0 7" fill="#6c757d" />
            </marker>
          </defs>
          <path d="M 5 20 L 50 20" stroke="#6c757d" strokeWidth="2" markerEnd="url(#arrowhead)" />
        </svg>
      </div>

      {/* Entry Products Column */}
      <div className="funnel-column funnel-entry">
        <div className="column-header">
          <span className="header-icon">🚪</span>
          <span>Entry Point</span>
        </div>
        <div className="column-items">
          {/* PROMO ENTRIES: Products with active promos (shown first, aligned with promo traffic) */}
          {(productsByPosition.entry.length > 0 ? productsByPosition.entry : entryPoints)
            .filter(product => getPromoForProduct(product.id))
            .map((product) => {
              const promo = getPromoForProduct(product.id);
              return (
                <div
                  key={`promo-${product.id}`}
                  className={`funnel-node product-node entry ${product.is_primary_tripwire ? 'tripwire' : ''} has-promo promo-entry clickable`}
                  onClick={() => navigate('/planned-promotions')}
                  title="Click to edit promotions"
                >
                  <span className="promo-badge">{promo.promotion_type === 'lifetime_deal' ? '🏷️ LTD' : '🔥 PROMO'}</span>
                  <span className="node-name">{product.name}</span>
                  <span className="node-price">
                    <span className="price-promo">{formatPrice(promo.sale_price)}</span>
                  </span>
                  <span className="node-sales-target promo-sales">
                    <strong>{promo.estimated_sales || 0}</strong> from promo
                  </span>
                </div>
              );
            })}

          {/* ORGANIC ENTRIES: All entry products for organic traffic (regular pricing) */}
          {(productsByPosition.entry.length > 0 ? productsByPosition.entry : entryPoints).map((product) => {
            const salesTarget = getSalesTarget(product.name);
            const isOneTime = product.purchase_type === 'one-time';
            return (
              <div
                key={`organic-${product.id}`}
                className={`funnel-node product-node entry ${product.is_primary_tripwire ? 'tripwire' : ''}`}
              >
                {product.is_primary_tripwire && <span className="tripwire-badge">Primary Tripwire</span>}
                <span className="node-name">{product.name}</span>
                <span className="node-price">{formatPrice(product.sale_price)}</span>
                {salesTarget && (
                  <span className="node-sales-target">
                    <strong>{salesTarget}</strong> {isOneTime ? 'organic' : 'sales/mo'}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Arrow: Entry → Core */}
      {productsByPosition.core.length > 0 && (
        <div className="flow-arrow">
          <svg viewBox="0 0 60 40" preserveAspectRatio="xMidYMid meet">
            <defs>
              <marker id="arrowhead-green" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                <polygon points="0 0, 10 3.5, 0 7" fill="#198754" />
              </marker>
            </defs>
            <path d="M 5 20 L 50 20" stroke="#198754" strokeWidth="2" markerEnd="url(#arrowhead-green)" />
          </svg>
          {upgradePaths.length > 0 && (
            <span className="conversion-label">
              {Math.round(upgradePaths.find(p =>
                productsByPosition.entry.some(e => e.id === p.from_product_id) &&
                productsByPosition.core.some(c => c.id === p.to_product_id)
              )?.conversion_rate || 0)}%
            </span>
          )}
        </div>
      )}

      {/* Core Products Column */}
      {productsByPosition.core.length > 0 && (
        <div className="funnel-column funnel-core">
          <div className="column-header">
            <span className="header-icon">💎</span>
            <span>Core Offer</span>
          </div>
          <div className="column-items">
            {productsByPosition.core.map((product) => {
              const promo = getPromoForProduct(product.id);
              const upgradeCount = getUpgradeCount[product.id];
              return (
                <div
                  key={product.id}
                  className={`funnel-node product-node core ${promo ? 'has-promo' : ''}`}
                >
                  {promo && (
                    <span className="promo-badge">{promo.promotion_type === 'lifetime_deal' ? 'LTD' : 'PROMO'}</span>
                  )}
                  <span className="node-name">{product.name}</span>
                  <span className="node-price">
                    {promo ? (
                      <>
                        <span className="price-original">{formatPrice(product.sale_price)}</span>
                        <span className="price-promo">{formatPrice(promo.sale_price)}</span>
                      </>
                    ) : (
                      formatPrice(product.sale_price)
                    )}
                  </span>
                  {upgradeCount > 0 && (
                    <span className="node-upgrade-count">
                      <strong>{upgradeCount}</strong> upgrades/mo
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Arrow: Core → Premium */}
      {productsByPosition.premium.length > 0 && (
        <div className="flow-arrow">
          <svg viewBox="0 0 60 40" preserveAspectRatio="xMidYMid meet">
            <defs>
              <marker id="arrowhead-gold" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                <polygon points="0 0, 10 3.5, 0 7" fill="#ffc107" />
              </marker>
            </defs>
            <path d="M 5 20 L 50 20" stroke="#ffc107" strokeWidth="2" markerEnd="url(#arrowhead-gold)" />
          </svg>
          {upgradePaths.length > 0 && (
            <span className="conversion-label">
              {Math.round(upgradePaths.find(p =>
                productsByPosition.core.some(c => c.id === p.from_product_id) &&
                productsByPosition.premium.some(pr => pr.id === p.to_product_id)
              )?.conversion_rate || 0)}%
            </span>
          )}
        </div>
      )}

      {/* Premium Products Column */}
      {productsByPosition.premium.length > 0 && (
        <div className="funnel-column funnel-premium">
          <div className="column-header">
            <span className="header-icon">👑</span>
            <span>Premium</span>
          </div>
          <div className="column-items">
            {productsByPosition.premium.map((product) => {
              const promo = getPromoForProduct(product.id);
              return (
                <div
                  key={product.id}
                  className={`funnel-node product-node premium ${promo ? 'has-promo' : ''}`}
                >
                  {promo && (
                    <span className="promo-badge">{promo.promotion_type === 'lifetime_deal' ? 'LTD' : 'PROMO'}</span>
                  )}
                  <span className="node-name">{product.name}</span>
                  <span className="node-price">
                    {promo ? (
                      <>
                        <span className="price-original">{formatPrice(product.sale_price)}</span>
                        <span className="price-promo">{formatPrice(promo.sale_price)}</span>
                      </>
                    ) : (
                      formatPrice(product.sale_price)
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Promotions are now shown in the Traffic Sources column on the left */}
    </div>
  );
};

export default FunnelDiagram;
